import { ContractArea } from "../../types";

import { RemoteData } from "@nvdb-rapport/shared/src/utils/remoteData";
import { ErrorData } from "../../update/ui";
import { Maybe, Nothing } from "purify-ts/Maybe";
import { VegSystem } from "@nvdb-rapport/shared/src/utils/vegfilter";
import { Either, Right } from "purify-ts/Either";
import { formatDate } from "@nvdb-rapport/shared/src/components/DatePicker";
import { GetInterface, Codec, array, string, number } from "purify-ts";
import * as R from "remeda";
import { VegobjektTypeList, VegobjektType } from "../../utils/vegobjekt";
import { ReadVersionOption } from "../FormComponents/ReadVersionSelect";

export type DriftskontraktModelProps = {
  driftskontraktState: DriftskontraktModel;
};
export type DriftskontraktActionProps = {
  driftskontraktDispatch: React.Dispatch<DriftskontraktAction>;
};

export const DriftskontraktType = Codec.interface({
  beskrivelse: string,
  id: number,
  navn: string,
  feature: string,
});

export type DriftskontraktType = GetInterface<typeof DriftskontraktType>;

export const DriftskontraktTypeList = array(DriftskontraktType);
export type DriftskontraktTypeList = GetInterface<
  typeof DriftskontraktTypeList
>;

export const CustomReportData = Codec.interface({
  omit: VegobjektTypeList,
  add: VegobjektTypeList,
});

export type CustomReportData = GetInterface<typeof CustomReportData>;

export interface DriftskontraktModel {
  contractAreas: RemoteData<ErrorData, ContractArea[]>;
  vegobjektTilvalg: RemoteData<ErrorData, VegobjektType[]>;
  vegobjektDefault: RemoteData<ErrorData, VegobjektType[]>;
  selectedVegobjekt: Maybe<VegobjektType>;
  selectedSecondaryVegobjekt: Maybe<VegobjektType>;
  selectedContractArea: Maybe<ContractArea>;
  reportSelection: "standard" | "egendefinert";
  reportTypes: DriftskontraktTypeList;
  date: Either<string, string>;
  reportTypeInput: string;
  selectedReportType: Maybe<DriftskontraktType>;
  customReport: CustomReportData;
  vegsystemInput: string;
  vegsystem: Either<string, VegSystem>;
  additionalSelections: boolean;
  showValidationErrors: boolean;
  selectedReadVersion: ReadVersionOption | null;
}

export const initialDriftskontraktModel: DriftskontraktModel = {
  contractAreas: { type: "NOT_ASKED" },
  vegobjektTilvalg: { type: "NOT_ASKED" },
  vegobjektDefault: { type: "NOT_ASKED" },
  customReport: { omit: [], add: [] },
  selectedVegobjekt: Nothing,
  selectedSecondaryVegobjekt: Nothing,
  vegsystemInput: "",
  vegsystem: Right([]),
  reportSelection: "standard",
  reportTypeInput: "",
  selectedReportType: Nothing,
  reportTypes: [],
  selectedContractArea: Nothing,
  additionalSelections: false,
  date: Right(formatDate(new Date())),
  showValidationErrors: false,
  selectedReadVersion: { label: "v4", value: true },
};

type SetReportSelection = {
  type: "SetReportSelection";
  payload: "standard" | "egendefinert";
};

type GotContractAreas = {
  type: "GotContractAreas";
  payload: RemoteData<ErrorData, ContractArea[]>;
};

type GotReportTypes = {
  type: "GotReportTypes";
  payload: RemoteData<ErrorData, DriftskontraktTypeList>;
};

type SelectReportType = {
  type: "SelectReportType";
  payload: Maybe<DriftskontraktType>;
};

type SelectContractArea = {
  type: "SelectContractArea";
  payload: Maybe<ContractArea>;
};

type Reset = {
  type: "Reset";
};

type ResetTilvalg = {
  type: "ResetTilvalg";
};

type UpdateVegsystem = {
  type: "UpdateVegsystem";
  payload: string;
};

type SetVegobjektTilvalg = {
  type: "SetVegobjektTilvalg";
  payload: RemoteData<ErrorData, VegobjektType[]>;
};

type SetVegobjectDefault = {
  type: "SetVegobjectDefault";
  payload: RemoteData<ErrorData, VegobjektType[]>;
};

type SelectOneVegobjekt = {
  type: "SelectOneVegobjekt";
  payload: Maybe<VegobjektType>;
};

type SelectSecondaryVegobjekt = {
  type: "SelectSecondaryVegobjekt";
  payload: Maybe<VegobjektType>;
};

type SetDate = {
  type: "SetDate";
  payload: Either<string, string>;
};

type UpdateCustomAdditions = {
  type: "UpdateCustomAdditions";
  payload: VegobjektType[];
};

type UpdateCustomRemovals = {
  type: "UpdateCustomRemovals";
  payload: VegobjektType[];
};

type AdditionalSelections = {
  type: "AdditionalSelections";
  payload: boolean;
};

type ShowValidationErrors = {
  type: "ShowValidationErrors";
  payload: boolean;
};

type SetReadVersion = {
  type: "SetReadVersion";
  payload: ReadVersionOption | null;
};

export type DriftskontraktAction =
  | SetReportSelection
  | GotContractAreas
  | GotReportTypes
  | SelectReportType
  | SetDate
  | SelectContractArea
  | Reset
  | UpdateVegsystem
  | SetVegobjektTilvalg
  | SetVegobjectDefault
  | SelectOneVegobjekt
  | SelectSecondaryVegobjekt
  | UpdateCustomAdditions
  | UpdateCustomRemovals
  | AdditionalSelections
  | ResetTilvalg
  | ShowValidationErrors
  | SetReadVersion;

export function searchReducer(
  model: DriftskontraktModel,
  action: DriftskontraktAction
): DriftskontraktModel {
  switch (action.type) {
    case "SetReportSelection":
      return { ...model, reportSelection: action.payload };
    case "GotContractAreas":
      return { ...model, contractAreas: action.payload };
    case "GotReportTypes":
      if (action.payload.type === "SUCCESS") {
        return { ...model, reportTypes: action.payload.data };
      } else {
        return model;
      }
    case "SelectReportType":
      return R.pipe(
        model,
        R.set("selectedReportType", action.payload),
        R.set("selectedVegobjekt", Nothing)
      );
    case "SelectContractArea":
      return { ...model, selectedContractArea: action.payload };
    case "SetDate":
      return { ...model, date: action.payload };
    case "Reset":
      return {
        ...initialDriftskontraktModel,
        contractAreas: model.contractAreas,
        reportTypes: model.reportTypes,
      };
    case "UpdateVegsystem":
      return R.pipe(
        model,
        R.set("vegsystemInput", action.payload),
        R.set("vegsystem", VegSystem.decode(action.payload.split(",")))
      );
    case "SetVegobjektTilvalg":
      return R.pipe(model, R.set("vegobjektTilvalg", action.payload));

    case "SetVegobjectDefault":
      return R.pipe(model, R.set("vegobjektDefault", action.payload));

    case "SelectOneVegobjekt":
      return R.pipe(model, R.set("selectedVegobjekt", action.payload));
    case "SelectSecondaryVegobjekt":
      return R.pipe(model, R.set("selectedSecondaryVegobjekt", action.payload));
    case "AdditionalSelections":
      return R.pipe(model, R.set("additionalSelections", action.payload));

    case "UpdateCustomAdditions":
      return R.pipe(
        model,
        R.set(
          "customReport",
          R.pipe(model.customReport, R.set("add", action.payload))
        )
      );
    case "UpdateCustomRemovals":
      return R.pipe(
        model,
        R.set(
          "customReport",
          R.pipe(model.customReport, R.set("omit", action.payload))
        )
      );
    case "ResetTilvalg":
      if (
        model.vegobjektDefault.type === "SUCCESS" &&
        model.vegobjektTilvalg.type === "SUCCESS"
      ) {
        return R.pipe(
          model,
          R.set(
            "customReport",
            R.pipe(
              model.customReport,
              R.set(
                "omit",
                setOmittedInitialOptions({
                  vegobjektTilvalg: model.vegobjektTilvalg,
                  vegobjektDefault: model.vegobjektDefault,
                }) ?? []
              ),
              R.set("add", model.vegobjektDefault.data)
            )
          )
        );
      } else return model;
    case "ShowValidationErrors":
      return R.pipe(model, R.set("showValidationErrors", action.payload));
    case "SetReadVersion":
      return R.pipe(model, R.set("selectedReadVersion", action.payload));
  }
}

export function setOmittedInitialOptions({
  vegobjektTilvalg,
  vegobjektDefault,
}: {
  vegobjektTilvalg: RemoteData<ErrorData, VegobjektType[]>;
  vegobjektDefault: RemoteData<ErrorData, VegobjektType[]>;
}) {
  if (
    vegobjektTilvalg.type === "SUCCESS" &&
    vegobjektDefault.type === "SUCCESS"
  ) {
    const tilvalgV4 = vegobjektTilvalg.data;
    const defaultV4 = vegobjektDefault.data;
    const objectsToRemove = R.map(defaultV4, o => o.id);
    const dataIds = R.map(tilvalgV4, o => o.id);
    const filteredIds = R.difference(dataIds, objectsToRemove);
    const filteredTilvalg = R.filter(tilvalgV4, o =>
      filteredIds.includes(o.id)
    );
    return filteredTilvalg;
  }
}
